import React from 'react';
import './ResetPasswordConfirm.scss';
import Copyright from './Copyright';
import { Card } from 'primereact/card';
import { Password } from 'primereact/password';
import { Button } from 'primereact/button';
import { useNavigate, useLocation } from 'react-router-dom';
import { PASSWORD_RESET_CONFIRM, PASSWORD_RESET_VALIDATE } from '../../api/urls';
import { normalizePasswordSimilarityError } from '../../utils/apiErrorTranslations';

const ResetPasswordConfirm: React.FC = () => {
    const navigate = useNavigate();
    const location = useLocation();

    const token = React.useMemo(() => {
        const pathSegments = location.pathname.split('/');
        const confirmIndex = pathSegments.findIndex(segment => segment === 'reset-password-confirm');
        if (confirmIndex !== -1 && confirmIndex + 1 < pathSegments.length) {
            return pathSegments[confirmIndex + 1];
        }
        return null;
    }, [location.pathname]);

    const [newPassword, setNewPassword] = React.useState('');
    const [confirmPassword, setConfirmPassword] = React.useState('');
    const [loading, setLoading] = React.useState(false);
    const [validating, setValidating] = React.useState(true);
    const [error, setError] = React.useState<string | null>(null);
    const [success, setSuccess] = React.useState<string | null>(null);

    React.useEffect(() => {
        const validateToken = async () => {
            if (!token) {
                setError('Brak tokenu resetowania hasła');
                setValidating(false);
                return;
            }

            try {
                const response = await fetch(PASSWORD_RESET_VALIDATE, {
                    method: 'POST',
                    headers: { 'Content-Type': 'application/json' },
                    body: JSON.stringify({ token }),
                });

                if (!response.ok) {
                    navigate('/reset-password-expired/', { replace: true });
                    return;
                }

                setValidating(false);
            } catch (err) {
                setError('Wystąpił błąd podczas walidacji tokenu');
                setValidating(false);
            }
        };

        validateToken();
    }, [token, navigate]);

    const onSubmit = async (e: React.FormEvent) => {
        e.preventDefault();
        setError(null);
        setSuccess(null);

        if (newPassword !== confirmPassword) {
            setError('Nowe hasła muszą się zgadzać.');
            return;
        }

        if (newPassword.length < 8) {
            setError('Nowe hasło musi mieć co najmniej 8 znaków.');
            return;
        }

        setLoading(true);
        try {
            const response = await fetch(PASSWORD_RESET_CONFIRM, {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({
                    token: token,
                    new_password: newPassword
                }),
            });

            if (!response.ok) {
                const errorData = await response.json();
                // Handle different error formats
                if (errorData.new_password && Array.isArray(errorData.new_password)) {
                    throw new Error(normalizePasswordSimilarityError(errorData.new_password[0]));
                } else if (errorData.token && Array.isArray(errorData.token)) {
                    throw new Error(errorData.token[0]);
                } else if (typeof errorData === 'string') {
                    throw new Error(errorData);
                } else {
                    throw new Error('Nie udało się zmienić hasła');
                }
            }

            setSuccess('Hasło zostało zmienione pomyślnie. Możesz się teraz zalogować.');
            setNewPassword('');
            setConfirmPassword('');

            // Redirect to login after 3 seconds
            setTimeout(() => {
                navigate('/login/', { replace: true });
            }, 3000);
        } catch (err: any) {
            setError(err?.message || 'Wystąpił błąd podczas zmiany hasła');
        } finally {
            setLoading(false);
        }
    };

    if (success) {
        return (
            <div className="reset-password-confirm-page pp-font-geist">
                <div className="reset-password-confirm-page__wrapper">
                    <div className="d-flex justify-content-center reset-password-confirm-page__logo">
                        <img
                            alt='mooveno-logo'
                            src={`/login/mooveno-logo-dark.svg`}
                            style={{ width: 182, height: 'auto', maxWidth: '90%', cursor: 'pointer' }}
                            onClick={() => navigate('/')}
                        />
                    </div>
                    <Card className="reset-password-confirm-page__card">
                        <div className="reset-password-confirm-page__header">
                            <h1 className="reset-password-confirm-page__title">Gotowe!</h1>
                            <p className="reset-password-confirm-page__subtitle">Twoje hasło zostało zaktualizowane.</p>
                        </div>
                        <Button
                            className="reset-password-confirm-page__submit"
                            label="Przejdź do logowania"
                            onClick={() => navigate('/login/', { replace: true })}
                        />
                    </Card>
                    <Copyright className="reset-password-confirm-page__copyright" />
                </div>
            </div>
        );
    }

    if (validating) {
        return (
            <div className="reset-password-confirm-page pp-font-geist">
                <div className="reset-password-confirm-page__wrapper">
                    <div className="d-flex justify-content-center reset-password-confirm-page__logo">
                        <img
                            alt='mooveno-logo'
                            src={`/login/mooveno-logo-dark.svg`}
                            style={{ width: 182, height: 'auto', maxWidth: '90%', cursor: 'pointer' }}
                            onClick={() => navigate('/')}
                        />
                    </div>
                    <Card className="reset-password-confirm-page__card">
                        <div className="d-flex flex-column align-items-center">
                            <i className="pi pi-spin pi-spinner" style={{ fontSize: '2rem' }}></i>
                            <h1 className="reset-password-confirm-page__title mt-3">Weryfikowanie...</h1>
                        </div>
                    </Card>
                </div>
            </div>
        );
    }

    return (
        <div className="reset-password-confirm-page pp-font-geist">
            <div className="reset-password-confirm-page__wrapper">
                <div className="d-flex justify-content-center reset-password-confirm-page__logo">
                    <img
                        alt='mooveno-logo'
                        src={`/login/mooveno-logo-dark.svg`}
                        style={{ width: 182, height: 'auto', maxWidth: '90%', cursor: 'pointer' }}
                        onClick={() => navigate('/')}
                    />
                </div>
                <Card className="reset-password-confirm-page__card">
                    <div className="reset-password-confirm-page__header">
                        <h1 className="reset-password-confirm-page__title">Nowe hasło</h1>
                        <p className="reset-password-confirm-page__subtitle">Wprowadź nowe hasło dla swojego konta.</p>
                    </div>
                    <form onSubmit={onSubmit} className="reset-password-confirm-page__form">
                        <div className="reset-password-confirm-page__field">
                            <label htmlFor="newPassword">Nowe hasło</label>
                            <Password
                                id="newPassword"
                                inputId="newPassword"
                                value={newPassword}
                                onChange={(e) => setNewPassword(e.target.value)}
                                placeholder="Wprowadź nowe hasło"
                                toggleMask
                                feedback={false}
                                className='w-100'
                                inputStyle={{ width: '100%' }}
                            />
                            <span className="reset-password-confirm-page__hint">Hasło musi mieć co najmniej 8 znaków</span>
                        </div>
                        <div className="reset-password-confirm-page__field">
                            <label htmlFor="confirmPassword">Potwierdź nowe hasło</label>
                            <Password
                                id="confirmPassword"
                                inputId="confirmPassword"
                                value={confirmPassword}
                                onChange={(e) => setConfirmPassword(e.target.value)}
                                placeholder="Potwierdź nowe hasło"
                                toggleMask
                                feedback={false}
                                className='w-100'
                                inputStyle={{ width: '100%' }}
                            />
                        </div>
                        <Button
                            className="reset-password-confirm-page__submit"
                            type='submit'
                            label={loading ? 'Zmienianie hasła…' : 'Zresetuj hasło'}
                            loading={loading}
                            disabled={!newPassword || !confirmPassword}
                        />
                        {error && <div className='text-danger' role='alert'>{error}</div>}
                    </form>
                </Card>
                <Copyright className="reset-password-confirm-page__copyright" />
            </div>
        </div>
    );
};

export default ResetPasswordConfirm; 